/**
 * Czech web-facing labels for shared enums + curated search aliases (vize
 * svetovy-katalog A1, řez 04).
 *
 * Moved here from apps/web/src/lib/menuData.ts so both the web AND the API
 * suggest endpoint can index them ("plavba" must find the cruises style even
 * over EN content). The EN `*_LABELS` maps in types/index.ts stay untouched —
 * admin and API keep consuming them; the admin i18n mirrors live in
 * apps/admin/src/i18n (enumLabels.ts) and are NOT related to these maps.
 *
 * Aliases are extra word forms the labels don't cover after normalization
 * (Czech inflection: "plavba" is not a substring of "plavby"). Keep them
 * SPARSE — obvious forms only, never a thesaurus. Matching is
 * `normalize(candidate).includes(normalize(query))` (see apps/api searchSuggest).
 */
import type { DateTolerance } from "./tripFilters";
import type {
  BlogCategory,
  TripType,
  TripStyle,
  DifficultyLevel,
  ActivityType,
  HotelFeature,
  HotelMealPlanType,
  InquiryDepartureChoice,
} from "./types";

/**
 * České názvy měsíců, index 0 = leden. Sdílí je mega menu, mobilní menu, panel filtrů
 * i klimatický pruh destinace — dřív existovaly čtyřikrát a stačilo přepsat jeden.
 * Pro `?month=` platí `MONTH_LABELS_CZ[month - 1]` (parametr je 1–12).
 */
export const MONTH_LABELS_CZ = [
  "Leden",
  "Únor",
  "Březen",
  "Duben",
  "Květen",
  "Červen",
  "Červenec",
  "Srpen",
  "Září",
  "Říjen",
  "Listopad",
  "Prosinec",
] as const;

/**
 * Slugy měsíců pro veřejné adresy `/kdy-vyrazit/<mesic>` (řez 8), index 0 = leden.
 *
 * Vlastní výčet, ne odvození z `MONTH_LABELS_CZ` přes odstranění diakritiky: adresa je
 * kontrakt a musí být stabilní i tehdy, když se popisek jednou přepíše na „Leden 2027".
 * Tvary jsou bez diakritiky, protože takhle vypadají odkazy na zbytku webu.
 */
export const MONTH_SLUGS_CZ = [
  "leden",
  "unor",
  "brezen",
  "duben",
  "kveten",
  "cerven",
  "cervenec",
  "srpen",
  "zari",
  "rijen",
  "listopad",
  "prosinec",
] as const;

/**
 * Měsíc v 6. pádě bez předložky („lednu" pro „v lednu"), index 0 = leden.
 *
 * Nadpisy stránky měsíce se jinak napsat nedají: „Kam v Leden" je znát na první pohled
 * a `Intl` skloňování měsíců nedělá. Výčet je krátký a konečný — pravidlo by bylo delší
 * než seznam a stejně by mělo výjimky (září, prosinci).
 */
export const MONTH_LOCATIVE_CZ = [
  "lednu",
  "únoru",
  "březnu",
  "dubnu",
  "květnu",
  "červnu",
  "červenci",
  "srpnu",
  "září",
  "říjnu",
  "listopadu",
  "prosinci",
] as const;

export type MonthSlug = (typeof MONTH_SLUGS_CZ)[number];

/** Číslo měsíce 1–12 → slug. Mimo rozsah = `null` (vstup chodí i z URL). */
export function monthSlug(month: number): MonthSlug | null {
  return Number.isInteger(month) && month >= 1 && month <= 12 ? MONTH_SLUGS_CZ[month - 1] : null;
}

/** Slug → číslo měsíce 1–12, nebo `null` u neznámého tvaru (adresa z ruky). */
export function monthFromSlug(slug: string): number | null {
  const index = (MONTH_SLUGS_CZ as readonly string[]).indexOf(slug);
  return index === -1 ? null : index + 1;
}

/**
 * Tři hlavní produkty katalogu — JEDINÉ místo, kde se jmenují (vize
 * doladeni-katalogu A1). Nejsou to odstíny jedné vlastnosti, ale tři různé věci
 * ke koupi, takže název musí říct rovnou CO to je: forma × prostředí.
 * Guard `tripTypeNamingGuard.test.ts` padá, jakmile někdo napíše vlastní znění.
 */
export const TRIP_TYPE_LABELS_CZ: Record<TripType, string> = {
  guided: "Poznávací s průvodcem",
  private: "Privátní na míru",
  resort: "Pobytové u moře",
};

/**
 * Nadpisová varianta téhož (H1 výpisu, `<title>`). Popisek je štítek („Poznávací
 * s průvodcem"), nadpis je věta o katalogu („Poznávací zájezdy s průvodcem") —
 * proto dvě mapy a ne lepení slova „zájezdy" na popisek, kde by vzniklo
 * „Pobytové u moře zájezdy".
 */
export const TRIP_TYPE_HEADINGS_CZ: Record<TripType, string> = {
  guided: "Poznávací zájezdy s průvodcem",
  private: "Privátní zájezdy na míru",
  resort: "Pobytové zájezdy u moře",
};

/**
 * Meta popis (`<meta name="description">`) výpisu zúženého na jeden typ.
 *
 * Třetí mapa klíčovaná `TripType`, a proto patří sem, k prvním dvěma: jsou to česká
 * znění typů v próze, jen delší. Když žila u stránky `/zajezdy`, guard názvosloví ji
 * NEVIDĚL (matchuje celé literály, ne věty), takže přejmenování typu nechalo tyhle tři
 * věty zvětralé se zeleným buildem — přesně to, čemu má kanonický domov bránit.
 */
export const TRIP_TYPE_META_DESCRIPTIONS_CZ: Record<TripType, string> = {
  guided:
    "Poznávací zájezdy s českým průvodcem — promyšlené trasy, ověřená místa a pevné termíny. Vyberte si destinaci i datum odjezdu.",
  private:
    "Privátní zájezdy na míru — trasu, tempo i hotely sestavíme přesně podle vás. Řekněte nám, kam chcete, a připravíme nabídku.",
  resort:
    "Pobytové zájezdy u moře — vyberte si destinaci, hotel a termín, přesnou cenu vám potvrdí náš specialista.",
};

/**
 * Krátký nárok pod popiskem typu na kartě úvodní stránky (vize H1).
 *
 * Čtvrtá mapa klíčovaná `TripType`, a proto patří sem, ke třem předchozím: je to české
 * znění typu v próze. Kdyby žila u komponenty, guard názvosloví ji NEVIDÍ (matchuje celé
 * literály, ne věty) a přejmenování typu by ji nechalo zvětralou se zeleným buildem.
 */
export const TRIP_TYPE_TAGLINES_CZ: Record<TripType, string> = {
  guided: "Pevné termíny, promyšlené trasy a průvodce po celou cestu",
  private: "Trasu, tempo i hotely sestavíme přesně podle vás",
  resort: "Hotel u moře, letenka a transfery v jednom",
};

/**
 * Sdělení „domluvím se" — druhý stupeň žebříčku nad typem zájezdu.
 *
 * „Poznávací s průvodcem" (`TRIP_TYPE_LABELS_CZ.guided`) říká, že v zájezdu VŮBEC nějaký
 * průvodce je; tohle říká, že mluví česky — a u cílovky 50+ je to otázka, která rozhoduje
 * o koupi. Nejsou to tedy dvě jména téhož a typ se kvůli tomu nepřejmenovává; jen to
 * sdělení mělo na webu tři různá znění („S ČESKÝM PRŮVODCEM" na kartě, „Český průvodce"
 * v rychlém náhledu, „Česky mluvící průvodce" na detailu), takže se žebříček nedal přečíst.
 *
 * Dvě konstanty jedné věty, týž vzor jako `TRIP_TYPE_LABELS_CZ` / `TRIP_TYPE_HEADINGS_CZ`.
 * Verzálky jsou DOSLOVA v hodnotě, ne přes CSS `text-transform` — pravidlo štítků karty
 * (`TripBadge` v `tripPresentation.ts`).
 *
 * Vědomě MIMO tuhle dvojici zůstává „S českým průvodcem" v `apps/web/src/lib/whyUs.ts`:
 * benefitová karta mluví o cestovní kanceláři obecně, ne o konkrétním zájezdu — je to
 * marketingové tvrzení, ne štítek z dat.
 */
export const CZECH_GUIDE_LABEL = "Česky mluvící průvodce";

/** Verzálková podoba téhož pro štítek na fotce karty (viz `CZECH_GUIDE_LABEL`). */
export const CZECH_GUIDE_BADGE_LABEL = "ČESKY MLUVÍCÍ PRŮVODCE";

/**
 * Popisky přepínače tolerance termínu (řez 02, vize E1). Hero hledač i panel filtrů
 * je čtou odtud, aby se obě afordance nikdy nerozešly ve znění ani v nabídce.
 */
export const DATE_TOLERANCE_LABELS_CZ: Record<DateTolerance, string> = {
  0: "přesně",
  1: "±1 den",
  3: "±3 dny",
  7: "±7 dní",
};

export const TRIP_STYLE_LABELS_CZ: Record<TripStyle, string> = {
  relaxation: "Relaxační a plážové",
  // NE „Poznávací" — to je od řezu 01 forma zájezdu (`guided`), ne styl.
  cultural: "Kultura a památky",
  adventure: "Dobrodružné a treking",
  family: "Rodinné",
  foodie: "Gastronomické",
  wellness: "Wellness",
  nature: "Příroda a safari",
  cruises: "Plavby",
};

export const DIFFICULTY_LABELS_CZ: Record<DifficultyLevel, string> = {
  easy: "Nenáročné",
  medium: "Středně náročné",
  hard: "Náročné",
};

/** Short single-word CZ labels for activity/inspiration type badges. */
export const ACTIVITY_TYPE_LABELS_CZ: Record<ActivityType, string> = {
  water: "Voda",
  adventure: "Dobrodružství",
  culture: "Kultura",
  nature: "Příroda",
  food: "Gastronomie",
  wellness: "Wellness",
};

/**
 * Kategorie blogu. Web je vypisoval anglicky (`BLOG_CATEGORY_LABELS` v utils), takže
 * u českého článku stálo „culture" — na výpisu, v detailu i u highlightu na destinaci.
 * EN mapa zůstává pro admin a API.
 */
export const BLOG_CATEGORY_LABELS_CZ: Record<BlogCategory, string> = {
  "travel-tips": "Tipy na cesty",
  culture: "Kultura",
  food: "Jídlo a gastronomie",
  nature: "Příroda",
  guides: "Průvodci",
  news: "Novinky",
};

/**
 * Popisek kategorie blogu pro hodnotu, která do webu chodí z API jako holý řetězec.
 * Neznámou kategorii vypíše, jak přišla — nikdy nevrací prázdno.
 */
export function blogCategoryLabel(category: string): string {
  return BLOG_CATEGORY_LABELS_CZ[category as BlogCategory] ?? category;
}

/**
 * Témata kontaktního formuláře `/kontakt` (vize F5.2) v pořadí voleb výběru: klíč je hodnota
 * pole `subject`, kterou formulář posílá do `POST /api/contact` (databáze a API zůstávají
 * anglicky), popisek je text volby. Webový parametr `tema` nese slug popisku
 * (`webParams.ts`, plocha `/kontakt`); `careers` míří z CTA „Staňte se naším průvodcem“.
 */
export const CONTACT_TOPIC_LABELS_CZ = {
  inquiry: "Dotaz k zájezdu",
  booking: "Rezervace",
  complaint: "Reklamace",
  corporate: "Firemní zájezd",
  careers: "Chci se stát průvodcem",
  other: "Jiné",
} as const satisfies Record<string, string>;

/** Téma kontaktního formuláře (klíč `CONTACT_TOPIC_LABELS_CZ`). */
export type ContactTopic = keyof typeof CONTACT_TOPIC_LABELS_CZ;

/** Hotel meal plans (D1) — sdílené webem i českým adminem (apps/admin/src/i18n/enumLabels.ts). */
export const HOTEL_MEAL_PLAN_LABELS_CZ: Record<HotelMealPlanType, string> = {
  none: "Bez stravy",
  breakfast: "Se snídaní",
  half_board: "Polopenze",
  full_board: "Plná penze",
  all_inclusive: "All inclusive",
};

/**
 * Zaměření hotelu (filtr pobytových) — sdílené webem i českým adminem.
 * „Jen pro dospělé" je celý důvod, proč dimenze vznikla: styl cestování ji vyjádřit neumí.
 */
export const HOTEL_FEATURE_LABELS_CZ: Record<HotelFeature, string> = {
  adults_only: "Jen pro dospělé",
  family: "Vhodné pro rodiny",
  beachfront: "Přímo u pláže",
  pool: "Bazén",
  spa: "Wellness a spa",
};

/** Keyed by the CUSTOMER choices (incl. "any") — the widget always offers "Nerozhoduje". */
export const HOTEL_DEPARTURE_LABELS_CZ: Record<InquiryDepartureChoice, string> = {
  prague: "Praha",
  vienna: "Vídeň",
  no_flights: "Bez letenek",
  any: "Nerozhoduje",
};

// ── Search aliases (suggest enum expansion) ──────────────────────────────────

// Pravidlo: alias JEN na tvar, který popisek nepokrývá (matching je
// `normalize(popisek).includes(normalize(dotaz))`). Proto `guided` alias nemá —
// „Poznávací s průvodcem" sám trefí „poznávací" i „průvodcem". `private`/`resort`
// ho mají na skloňované dvojslovné tvary, které v popisku nejsou
// (`normalize("Privátní na míru")` neobsahuje „privatni zajezd").
export const TRIP_TYPE_SEARCH_ALIASES: Partial<Record<TripType, string[]>> = {
  private: ["soukromý zájezd", "privátní zájezd"],
  resort: ["pobytový zájezd"],
};

export const TRIP_STYLE_SEARCH_ALIASES: Partial<Record<TripStyle, string[]>> = {
  cruises: ["plavba", "plavby lodí"],
};

/** "plavba" must hit water activities too — neither "Voda" nor the EN label contains it. */
export const ACTIVITY_TYPE_SEARCH_ALIASES: Partial<Record<ActivityType, string[]>> = {
  water: ["plavba", "plavby"],
};

// NOTE: difficulty has NO alias map on purpose — "snadný/střední/náročný" are covered
// by DIFFICULTY_LABELS_CZ substring matching. `enumMatcher` (apps/api) therefore takes
// `aliases` as optional; an empty map exported, imported and iterated for zero effect
// only pretended a curated list existed.
